import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useEmail } from '../../../contexts/EmailContext';

const { FiMail, FiEdit3, FiSave, FiX, FiCopy, FiInfo, FiPlus, FiList } = FiIcons;

const EmailTemplateManager = ({ onClose }) => {
  const { emailSettings, saveEmailSettings } = useEmail();
  
  // KOMPLETT LISTE OVER MALER (Inkludert de nye du ba om)
  const defaultTemplates = [
    {
      id: 'welcome',
      name: 'Velkomst E-post (Ny Bruker)',
      emailJsId: 'template_welcome',
      subject: 'Velkommen til HMSvarsling!',
      description: 'Sendes til nye brukere når konto opprettes.',
      variables: ['{{user_name}}', '{{company_name}}', '{{login_url}}', '{{user_type}}'],
      body: `Hei {{user_name}},\n\nVelkommen som ny {{user_type}} hos {{company_name}}!\nDin konto er nå opprettet.\n\nDu kan logge inn her:\n{{login_url}}\n\nMvh,\nHMSvarsling Teamet`
    },
    {
      id: 'newReport',
      name: 'Nytt Varsel (Til Bedrift)',
      emailJsId: 'template_new_report',
      subject: 'Nytt varsel mottatt: {{report_type}}',
      description: 'Sendes til bedriftens e-post når et nytt varsel kommer inn.',
      variables: ['{{company_name}}', '{{report_type}}', '{{report_id}}', '{{report_url}}'],
      body: `Hei {{company_name}},\n\nEn ny {{report_type}} har blitt registrert i systemet.\nSaksnummer: {{report_id}}\n\nLogg inn for å behandle saken:\n{{report_url}}\n\nVennlig hilsen,\nDitt HMS-system`
    },
    {
      id: 'receiptReporter',
      name: 'Kvittering til Varsler',
      emailJsId: 'template_receipt',
      subject: 'Bekreftelse på mottatt varsel',
      description: 'Sendes til varsleren som bekreftelse (hvis e-post er oppgitt).',
      variables: ['{{report_id}}', '{{company_name}}', '{{login_url}}'],
      body: `Hei,\n\nVi bekrefter at ditt varsel til {{company_name}} er mottatt.\nSaksnummer: {{report_id}}\n\nHvis du vil følge saken eller kommunisere med saksbehandler, kan du logge inn her:\n{{login_url}}\n\nMvh,\nVarslingsmottaket`
    },
    {
      id: 'inviteUser',
      name: 'Invitasjon til Kollega',
      emailJsId: 'template_invite',
      subject: 'Du er invitert til HMSvarsling',
      description: 'Sendes når admin inviterer en ny ansatt.',
      variables: ['{{inviter_name}}', '{{company_name}}', '{{invite_link}}'],
      body: `Hei,\n\n{{inviter_name}} har invitert deg til å bli med i HMS-systemet for {{company_name}}.\n\nKlikk her for å sette opp din bruker:\n{{invite_link}}`
    },
    {
      id: 'investigationAssigned',
      name: 'Tildeling av Sak',
      emailJsId: 'template_investigation_assigned',
      subject: 'Du er tildelt en sak: {{report_id}}',
      description: 'Sendes til saksbehandler når de settes som ansvarlig.',
      variables: ['{{investigator_name}}', '{{report_id}}', '{{company_name}}', '{{deadline}}'],
      body: `Hei {{investigator_name}},\n\nDu har blitt tildelt ansvaret for å utrede sak #{{report_id}} hos {{company_name}}.\n\nFrist for konklusjon: {{deadline}}`
    },
    {
      id: 'deadlineReminder',
      name: 'Fristvarsel',
      emailJsId: 'template_reminder_deadline',
      subject: 'Påminnelse: Frist utløper snart',
      description: 'Sendes automatisk når en frist nærmer seg.',
      variables: ['{{report_id}}', '{{days_left}}', '{{deadline}}', '{{company_name}}'],
      body: `Hei,\n\nDette er en påminnelse om at fristen for sak #{{report_id}} utløper om {{days_left}} dager.`
    },
    {
      id: 'subscriptionConfirmation',
      name: 'Abonnementsbekreftelse',
      emailJsId: 'template_company_registration',
      subject: 'Velkommen som kunde - Ordrebekreftelse',
      description: 'Sendes når en bedrift kjøper en pakke.',
      variables: ['{{company_name}}', '{{admin_name}}', '{{subscription_plan}}', '{{login_url}}'],
      body: `Hei {{admin_name}},\n\nTakk for at dere valgte HMSvarsling!\n\nDeres abonnement på {{subscription_plan}} er nå aktivert.`
    },
    {
      id: 'passwordReset',
      name: 'Glemt Passord',
      emailJsId: 'template_reset',
      subject: 'Tilbakestill ditt passord',
      description: 'Sendes når bruker ber om nytt passord.',
      variables: ['{{user_name}}', '{{reset_url}}'],
      body: `Hei {{user_name}},\n\nVi har mottatt en forespørsel om å tilbakestille passordet ditt.\n\nKlikk her for å lage nytt passord:\n{{reset_url}}`
    }
  ];

  const [templates, setTemplates] = useState(emailSettings.templates || defaultTemplates);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({ ...template });
  };

  const handleSave = (e) => {
    e.preventDefault();
    const updatedTemplates = templates.map(t => t.id === formData.id ? formData : t);
    setTemplates(updatedTemplates);
    saveEmailSettings({
      ...emailSettings,
      templates: updatedTemplates
    });
    setEditingTemplate(null);
  };

  const copyVar = (variable) => {
    navigator.clipboard.writeText(variable);
    alert(`Kopiert: ${variable}`);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full flex flex-col">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-xl">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiMail} className="text-blue-600" />
            E-post Maler
          </h2>
          <p className="text-sm text-gray-500">Alle systemets e-poster og EmailJS koblinger</p>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full">
          <SafeIcon icon={FiX} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {templates.map((template) => (
            <motion.div 
              key={template.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow bg-white"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-bold text-gray-900">{template.name}</h3>
                  <p className="text-xs text-gray-500">{template.description}</p>
                </div>
                <button 
                  onClick={() => handleEdit(template)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
                >
                  <SafeIcon icon={FiEdit3} /> Rediger
                </button>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-gray-500 uppercase">EmailJS ID</span>
                  <code className="text-sm font-mono text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-100">
                    {template.emailJsId}
                  </code>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {editingTemplate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto flex flex-col"
            >
              <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                <h3 className="text-lg font-bold text-gray-900">Rediger Mal</h3>
                <button onClick={() => setEditingTemplate(null)}><SafeIcon icon={FiX} /></button>
              </div>
              
              <form onSubmit={handleSave} className="p-6 space-y-6 flex-1 overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Navn</label>
                  <input 
                    type="text" 
                    value={formData.name} 
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg p-2"
                  />
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <label className="block text-sm font-bold text-yellow-900 mb-1">EmailJS Template ID *</label>
                  <input 
                    type="text" 
                    value={formData.emailJsId} 
                    onChange={e => setFormData({...formData, emailJsId: e.target.value})}
                    className="w-full border border-yellow-400 rounded-lg p-2 font-mono text-yellow-900 bg-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Variabler (Klikk for å kopiere)</label>
                  <div className="flex flex-wrap gap-2">
                    {formData.variables?.map(v => (
                      <button 
                        key={v} 
                        type="button"
                        onClick={() => copyVar(v)}
                        className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded border border-blue-200 hover:bg-blue-200 transition-colors flex items-center gap-1"
                      >
                        <SafeIcon icon={FiCopy} /> {v}
                      </button>
                    ))}
                  </div>
                </div>
              </form>

              <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setEditingTemplate(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Avbryt
                </button>
                <button 
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <SafeIcon icon={FiSave} /> Lagre
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmailTemplateManager;